/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { get, post } from '../../../services/GenesisApi';
import {
  TERMINALAPI,
  SUBTERMINALAPI,
  LDZAPI,
  EXITPOINTAPI,
} from '../../../constants/Maintenance';
import {
  ISubTerminalItem,
  IExitPointItem,
  ITerminalItem,
} from '../../../types/Locations.type';
import { ILDZItem } from '../../../types/LDZ.type';

interface ITerminalData {
  data: ITerminalItem[];
  totalRecords: number;
}

interface ISubTerminalData {
  data: ISubTerminalItem[];
  totalRecords: number;
}

interface IExitPointData {
  data: IExitPointItem[];
  totalRecords: number;
}

interface ILDZData {
  data: ILDZItem[];
  totalRecords: number;
}

interface ITerminalDataAdvSearch {
  data: ITerminalItem[];
  totalRecords: number;
  isLoadingAdvSearch: boolean;
}

interface ILDZDataAdvSearch {
  data: ILDZItem[];
  totalRecords: number;
  isLoadingAdvSearch: boolean;
}

export const getTerminalData = createAsyncThunk(
  'locationsData/getTerminalData',
  async () => {
    return get(TERMINALAPI, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getSubTerminalData = createAsyncThunk(
  'locationsData/getSubTerminalData',
  async () => {
    return get(SUBTERMINALAPI, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getLDZData = createAsyncThunk(
  'locationsData/getLDZData',
  async () => {
    return get(LDZAPI, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getExitPointData = createAsyncThunk(
  'locationsData/getExitPointData',
  async () => {
    return get(EXITPOINTAPI, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getSubTerminalDataAdvSearch = createAsyncThunk(
  'locationsData/getSubTerminalDataAdvSearch',
  async (requestBody: any) => {
    return post(`${SUBTERMINALAPI}/search`, requestBody)
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getExitPointDataAdvSearch = createAsyncThunk(
  'locationsData/getExitPointDataAdvSearch',
  async (requestBody: any) => {
    return post(`${EXITPOINTAPI}/search`, requestBody)
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export interface ILocationsState {
  terminalData: ITerminalData;
  subTerminalData: ISubTerminalData;
  ldzData: ILDZData;
  exitPointData: IExitPointData;
  isLoading: boolean;
  subTerminalDataAdvSearch: ITerminalDataAdvSearch;
  exitPointDataAdvSearch: ILDZDataAdvSearch;
}

export const initialState: ILocationsState = {
  terminalData: {
    data: [],
    totalRecords: 0,
  },
  subTerminalData: {
    data: [],
    totalRecords: 0,
  },
  ldzData: {
    data: [],
    totalRecords: 0,
  },
  exitPointData: {
    data: [],
    totalRecords: 0,
  },
  subTerminalDataAdvSearch: {
    data: [],
    totalRecords: 0,
    isLoadingAdvSearch: false,
  },
  exitPointDataAdvSearch: {
    data: [],
    totalRecords: 0,
    isLoadingAdvSearch: false,
  },
  isLoading: false,
};

const Locations = createSlice({
  name: 'locationsActions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTerminalData.pending, (state: ILocationsState) => {
        state.isLoading = true;
      })
      .addCase(
        getTerminalData.fulfilled,
        (
          state: ILocationsState,
          { payload }: PayloadAction<ITerminalItem[]>
        ) => {
          state.terminalData.data = payload;
          state.terminalData.totalRecords = payload?.length;
          state.isLoading = false;
        }
      )
      .addCase(getTerminalData.rejected, (state: ILocationsState) => {
        state.isLoading = false;
      })
      .addCase(getSubTerminalData.pending, (state: ILocationsState) => {
        state.isLoading = true;
      })
      .addCase(
        getSubTerminalData.fulfilled,
        (
          state: ILocationsState,
          { payload }: PayloadAction<ISubTerminalItem[]>
        ) => {
          state.subTerminalData.data = payload;
          state.subTerminalData.totalRecords = payload?.length;
          state.isLoading = false;
        }
      )
      .addCase(getSubTerminalData.rejected, (state: ILocationsState) => {
        state.isLoading = false;
      })
      .addCase(getLDZData.pending, (state: ILocationsState) => {
        state.isLoading = true;
      })
      .addCase(
        getLDZData.fulfilled,
        (state: ILocationsState, { payload }: PayloadAction<ILDZItem[]>) => {
          state.ldzData.data = payload;
          state.ldzData.totalRecords = payload?.length;
          state.isLoading = false;
        }
      )
      .addCase(getLDZData.rejected, (state: ILocationsState) => {
        state.isLoading = false;
      })
      .addCase(getExitPointData.pending, (state: ILocationsState) => {
        state.isLoading = true;
      })
      .addCase(
        getExitPointData.fulfilled,
        (
          state: ILocationsState,
          { payload }: PayloadAction<IExitPointItem[]>
        ) => {
          state.exitPointData.data = payload;
          state.exitPointData.totalRecords = payload?.length;
          state.isLoading = false;
        }
      )
      .addCase(getExitPointData.rejected, (state: ILocationsState) => {
        state.isLoading = false;
      })
      .addCase(
        getSubTerminalDataAdvSearch.pending,
        (state: ILocationsState) => {
          state.subTerminalDataAdvSearch.isLoadingAdvSearch = true;
        }
      )
      .addCase(
        getSubTerminalDataAdvSearch.fulfilled,
        (
          state: ILocationsState,
          { payload }: PayloadAction<ITerminalItem[]>
        ) => {
          state.subTerminalDataAdvSearch.data = payload;
          state.subTerminalDataAdvSearch.totalRecords = payload?.length;
          state.subTerminalDataAdvSearch.isLoadingAdvSearch = false;
        }
      )
      .addCase(
        getSubTerminalDataAdvSearch.rejected,
        (state: ILocationsState) => {
          state.subTerminalDataAdvSearch.isLoadingAdvSearch = false;
        }
      )
      .addCase(getExitPointDataAdvSearch.pending, (state: ILocationsState) => {
        state.exitPointDataAdvSearch.isLoadingAdvSearch = true;
      })
      .addCase(
        getExitPointDataAdvSearch.fulfilled,
        (state: ILocationsState, { payload }: PayloadAction<ILDZItem[]>) => {
          state.exitPointDataAdvSearch.data = payload;
          state.exitPointDataAdvSearch.totalRecords = payload?.length;
          state.exitPointDataAdvSearch.isLoadingAdvSearch = false;
        }
      )
      .addCase(getExitPointDataAdvSearch.rejected, (state: ILocationsState) => {
        state.exitPointDataAdvSearch.isLoadingAdvSearch = false;
      });
  },
});

export const locationsActions = Locations.actions;
export default Locations.reducer;
